/**
 * useTravelAwareFlag — small hook reading the per-instructor
 * Travel-Aware V1 feature flag from `/api/scheduling/prefs`.
 *
 * Feature flag rollout per `TRAVEL_AWARE_SCHEDULING_PROPOSAL.md`
 * §8.5: off by default. Operator opts in by toggling
 * `travel_aware_v1_enabled` on their preferences row.
 *
 * Returns: { enabled, ready, prefs }
 *   • enabled — true ONLY when prefs.travel_aware_v1_enabled is true.
 *   • ready   — true once the prefs fetch resolves (success OR fail).
 *   • prefs   — the raw prefs payload for downstream consumers
 *               (e.g. buffer_minutes for the calculator).
 *
 * Calm-baseline behaviour:
 *   • Any error → enabled=false, ready=true.
 *   • Refetch is opt-in via the returned `reload()` function.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function useTravelAwareFlag() {
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady]     = useState(false);
  const [prefs, setPrefs]     = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/scheduling/prefs`, {
        withCredentials: true,
      });
      setPrefs(r.data);
      setEnabled(Boolean(r.data?.travel_aware_v1_enabled));
    } catch {
      setEnabled(false);
      setPrefs(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      await reload();
      if (!alive) return;
    })();
    return () => { alive = false; };
  }, [reload]);

  return { enabled, ready, prefs, reload };
}
